import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getDocumentText } from '@/shared/office/word'
import { getDocumentIdentity, type DocumentIdentity } from '@/shared/office/host'
import {
    createDocumentScan,
    getScanResultsHighlights,
    rescanDocumentScanResult,
    searchChecklistItems,
    searchDocumentScanResults,
    searchScanResults,
    ScanHttpError
} from './api'
import { getScanCache, normalizeFilters, patchScanCache, type ScanFilters } from './scanCache'
import type { ChecklistItem, ScanResult } from './types'

const POLL_INTERVAL_MS = 3000

/** Server-side `archived` filter for the current scope (none when 'all'). */
const archivedFilters = (filters: ScanFilters): Array<Record<string, unknown>> =>
  filters.archived === 'all'
    ? []
    : [{ field: 'archived', comparisonOperator: 'eq', value: filters.archived === 'archived' }]

export interface UseChecklistScan {
  /** Open document's file name, for the history header (undefined when unsaved). */
  documentName?: string
  // History (the initial view) — every WORD scan row for the open document.
  results: ScanResult[]
  loadingHistory: boolean
  historyError: string | null
  reloadHistory: () => void
  /** Silently refresh both lists (used when re-entering the scan view). */
  refresh: () => void
  pending: boolean
  /** Load findings (textBlocks/explanation) for rows whose detail page opened. */
  ensureHighlights: (resultIds: string[]) => void
  /** Rescan the given rows (the errored ones in a run) against the live text. */
  rescanItems: (resultIds: string[]) => Promise<void>
  // Checklist-item picker (rendered in the scan drawer).
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  items: ChecklistItem[]
  loadingItems: boolean
  itemsError: string | null
  query: string
  setQuery: (value: string) => void
  filters: ScanFilters
  setFilters: (filters: ScanFilters) => void
  selectedIds: Set<string>
  toggleSelect: (id: string) => void
  reloadItems: () => void
  scanning: boolean
  scanError: string | null
  runScan: () => Promise<void>
}

const isPendingRow = (row: ScanResult): boolean => row.status === 'INITIAL'

const errorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ScanHttpError) return error.message
  return error instanceof Error ? error.message : fallback
}

/** Read the live Word text, rejecting an empty document before dispatching. */
const readDocumentText = async (): Promise<string> => {
  const text = await getDocumentText()
  if (!text.trim()) {
    throw new Error('The document appears to be empty — nothing to scan.')
  }
  return text
}

/**
 * Merge a fresh history snapshot over the current rows. Optimistic in-flight
 * rows the server hasn't returned yet stay on top; for rows that are unchanged
 * (same status + updatedAt) the already-loaded findings are preserved, so a
 * background refresh never drops highlights (the search omits them) or causes a
 * full highlights refetch / chevron flicker. Names resolved earlier are kept.
 */
const mergeHistoryRows = (fresh: ScanResult[], prev: ScanResult[]): ScanResult[] => {
  const prevById = new Map(prev.filter((row) => row._id).map((row) => [row._id!, row]))
  const fetchedIds = new Set(fresh.map((row) => row._id))
  const keptInFlight = prev.filter((row) => isPendingRow(row) && (!row._id || !fetchedIds.has(row._id)))

  const merged = fresh.map((row) => {
    const existing = row._id ? prevById.get(row._id) : undefined
    if (!existing) return row
    const name = existing.name ?? row.name
    if (existing.highlightsLoaded && existing.status === row.status && existing.updatedAt === row.updatedAt) {
      return {
        ...row,
        name,
        explanation: existing.explanation,
        textBlocks: existing.textBlocks,
        highlightsLoaded: true
      }
    }
    return { ...row, name }
  })

  return [...keptInFlight, ...merged]
}

/**
 * Drives the checklist-item scan pane. The initial view is the open document's
 * scan history (every WORD-source row for it, grouped by run in the view); a
 * scan is launched from a picker drawer, after which its pending rows show at
 * the top of the history and are polled until they resolve. Mirrors the
 * ramp-web-app Drive scanner's per-form history + poll/merge flow.
 */
export function useChecklistScan(): UseChecklistScan {
  // Seed picker state from the session cache so switching to the chat and back
  // restores the user's query, filters and selections (see scanCache.ts).
  const cached = getScanCache()
  const [items, setItems] = useState<ChecklistItem[]>(cached.items)
  const [loadingItems, setLoadingItems] = useState(false)
  const [itemsError, setItemsError] = useState<string | null>(null)
  const [query, setQueryState] = useState(cached.query)
  const [filters, setFiltersState] = useState<ScanFilters>(cached.filters)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(cached.selectedIds))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)

  const [results, setResults] = useState<ScanResult[]>([])

  const [loadingHistory, setLoadingHistory] = useState(true)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [documentMeta, setDocumentMeta] = useState<DocumentIdentity | null>(null)

  const resultsRef = useRef<ScanResult[]>(results)
  resultsRef.current = results

  // Rows currently being rescanned, mapped to their `updatedAt` at dispatch.
  // A rescan keeps the row at COMPLETE server-side, so it counts as resolved
  // only once `updatedAt` moves past this baseline (not on a status flip).
  const rescanBaselineRef = useRef<Map<string, string | undefined>>(new Map())
  // Ids whose highlights are being fetched, so the batch effect doesn't re-request.
  const highlightsInFlightRef = useRef<Set<string>>(new Set())

  // Persist query/filters/selection to the cache as they change.
  const setQuery = useCallback((value: string) => {
    setQueryState(value)
    patchScanCache({ query: value })
  }, [])
  const setFilters = useCallback((next: ScanFilters) => {
    const normalized = normalizeFilters(next)
    setFiltersState(normalized)
    patchScanCache({ filters: normalized })
  }, [])

  const archivedScope = filters.archived

  // `silent` keeps the current list visible and skips the spinner/error — used
  // for the background refresh when re-entering the scan view.
  const reloadItems = useCallback((opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true
    let cancelled = false
    if (!silent) {
      setLoadingItems(true)
      setItemsError(null)
    }
    searchChecklistItems(archivedFilters({ ...getScanCache().filters, archived: archivedScope }))
      .then((fetched) => {
        if (cancelled) return
        setItems(fetched)
        patchScanCache({ items: fetched, loaded: true })
      })
      .catch((error) => {
        if (!cancelled && !silent) setItemsError(errorMessage(error, 'Failed to load checklist items.'))
      })
      .finally(() => {
        if (!cancelled && !silent) setLoadingItems(false)
      })
    return () => {
      cancelled = true
    }
  }, [archivedScope])

  // Fetch items once per session — cached items make the chat round-trip instant
  // and let the history resolve checklist-item names without a reload spinner.
  useEffect(() => {
    if (getScanCache().loaded) return undefined
    return reloadItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The archived scope is a server-side filter — refetch when it changes.
  const prevArchivedRef = useRef(archivedScope)
  useEffect(() => {
    if (prevArchivedRef.current === archivedScope) return undefined
    prevArchivedRef.current = archivedScope
    return reloadItems()
  }, [archivedScope, reloadItems])

  // Resolve the open document's identity once: it scopes the history fetch and
  // rides along on each scan as provenance.
  useEffect(() => {
    let cancelled = false
    getDocumentIdentity()
      .then((meta) => {
        if (!cancelled) setDocumentMeta(meta)
      })
      .catch(() => {
        if (!cancelled) setDocumentMeta({ hostName: 'Word' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  const fetchHistory = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!documentMeta) return undefined
      const silent = opts?.silent === true
      let cancelled = false
      if (!silent) {
        setLoadingHistory(true)
        setHistoryError(null)
      }
      searchDocumentScanResults(documentMeta)
        .then((rows) => {
          if (!cancelled) setResults((prev) => mergeHistoryRows(rows, prev))
        })
        .catch((error) => {
          if (!cancelled && !silent) setHistoryError(errorMessage(error, 'Failed to load scan history.'))
        })
        .finally(() => {
          if (!cancelled && !silent) setLoadingHistory(false)
        })
      return () => {
        cancelled = true
      }
    },
    [documentMeta]
  )

  const reloadHistory = useCallback(() => {
    fetchHistory()
  }, [fetchHistory])

  // Load the history as soon as the document identity is known.
  useEffect(() => fetchHistory(), [fetchHistory])

  // Background refresh on re-entering the scan view (the pane stays mounted, so
  // state persists) — keep both lists current without a loading flash.
  const refresh = useCallback(() => {
    fetchHistory({ silent: true })
    reloadItems({ silent: true })
  }, [fetchHistory, reloadItems])

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      patchScanCache({ selectedIds: [...next] })
      return next
    })
  }, [])

  const itemsById = useMemo(() => new Map(items.map((item) => [item._id, item])), [items])

  const openDrawer = useCallback(() => {
    setScanError(null)
    setDrawerOpen(true)
  }, [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  const runScan = useCallback(async () => {
    const checklistItemIds = [...selectedIds]
    if (!checklistItemIds.length) return
    setScanning(true)
    setScanError(null)
    try {
      const [text, metadata] = await Promise.all([readDocumentText(), getDocumentIdentity()])
      const response = await createDocumentScan({ checklistItemIds, text, metadata })
      const createdAt = new Date().toISOString()
      // Surface the new run's rows at the top of the history immediately; the
      // poll fills in their verdicts and highlights as they resolve.
      const fresh: ScanResult[] = response.scanResults.map((row) => ({
        _id: row._id,
        checklistItemId: row.checklistItemId,
        checklistItemScanId: response.checklistItemScanId,
        name: itemsById.get(row.checklistItemId)?.name,
        status: row.status,
        createdAt
      }))
      setResults((prev) => [...fresh, ...prev])
      setSelectedIds(new Set())
      patchScanCache({ selectedIds: [] })
      setDrawerOpen(false)
    } catch (error) {
      setScanError(errorMessage(error, 'Failed to start the scan.'))
    } finally {
      setScanning(false)
    }
  }, [selectedIds, itemsById])

  // Re-run the given rows (a run's errored items) against the current document
  // text. The rows flip back to INITIAL so the poll picks up their refreshed
  // verdicts; the live text is read once and re-sent for each (WORD rows persist
  // no text). Mirrors the ramp-web-app scanner's "Rescan N Items" action.
  const rescanItems = useCallback(async (resultIds: string[]) => {
    const ids = [...new Set(resultIds.filter(Boolean))]
    if (!ids.length) return
    const idSet = new Set(ids)
    setScanError(null)
    ids.forEach((id) => rescanBaselineRef.current.set(id, resultsRef.current.find((row) => row._id === id)?.updatedAt))
    setResults((prev) =>
      prev.map((row) =>
        row._id && idSet.has(row._id)
          ? {
              ...row,
              status: 'INITIAL',
              scanStatus: undefined,
              explanation: undefined,
              textBlocks: undefined,
              highlightsLoaded: false
            }
          : row
      )
    )
    try {
      const text = await readDocumentText()
      await Promise.all(ids.map((id) => rescanDocumentScanResult(id, text)))
    } catch (error) {
      ids.forEach((id) => rescanBaselineRef.current.delete(id))
      setScanError(errorMessage(error, 'Failed to rescan.'))
      setResults((prev) => prev.map((row) => (row._id && idSet.has(row._id) ? { ...row, status: 'ERROR' } : row)))
    }
  }, [])

  // Fetch highlights (textBlocks + explanation) lazily for the given rows —
  // called when a scan's detail page opens, so we only pull findings for the run
  // being viewed instead of every resolved row in the whole history up front.
  // Verdicts/colors come from the search rows, so the list renders without this.
  // De-dupes via the in-flight ref and skips rows already loaded or still
  // pending, so re-renders and poll ticks don't re-request.
  const ensureHighlights = useCallback((resultIds: string[]) => {
    const current = resultsRef.current
    const ids = resultIds.filter((id) => {
      if (!id || highlightsInFlightRef.current.has(id)) return false
      const row = current.find((entry) => entry._id === id)
      return !!row && !row.highlightsLoaded && !isPendingRow(row)
    })
    if (!ids.length) return
    const idSet = new Set(ids)
    ids.forEach((id) => highlightsInFlightRef.current.add(id))
    getScanResultsHighlights(ids)
      .then((highlights) => {
        const byId = new Map(highlights.map((entry) => [entry._id, entry]))
        setResults((prev) =>
          prev.map((row) =>
            row._id && idSet.has(row._id)
              ? {
                  ...row,
                  highlightsLoaded: true,
                  scanStatus: byId.get(row._id)?.scanStatus ?? row.scanStatus,
                  explanation: byId.get(row._id)?.explanation,
                  textBlocks: byId.get(row._id)?.textBlocks ?? []
                }
              : row
          )
        )
      })
      .catch(() => {
        /* Leave rows un-flagged so a later open retries. */
      })
      .finally(() => ids.forEach((id) => highlightsInFlightRef.current.delete(id)))
  }, [])

  const pending = useMemo(() => results.some(isPendingRow), [results])

  // While any row is INITIAL, poll the runs those rows belong to (a scan can
  // span runs once historical rows are rescanned) and merge their status. The
  // highlights effect then fills in findings for the rows that just resolved.
  useEffect(() => {
    if (!pending) return undefined

    let cancelled = false
    const poll = async () => {
      const scanIds = [
        ...new Set(
          resultsRef.current
            .filter((row) => isPendingRow(row) && row.checklistItemScanId)
            .map((row) => row.checklistItemScanId!)
        )
      ]
      if (!scanIds.length) return
      try {
        const freshRows = await searchScanResults(scanIds)
        const freshById = new Map(freshRows.map((row) => [row._id, row]))

        // A row counts as resolved once the server row is no longer INITIAL —
        // except a rescan in flight, which holds the row at COMPLETE and is only
        // done when `updatedAt` has moved past the baseline captured at dispatch.
        const baselines = rescanBaselineRef.current
        const isResolved = (next: ScanResult | undefined): boolean => {
          if (!next || isPendingRow(next)) return false
          if (!baselines.has(next._id!)) return true
          return next.updatedAt !== undefined && next.updatedAt !== baselines.get(next._id!)
        }

        // A resolved rescan is no longer in flight — let it merge normally below.
        resultsRef.current
          .filter((row) => row._id && baselines.has(row._id) && isResolved(freshById.get(row._id!)))
          .forEach((row) => baselines.delete(row._id!))

        if (cancelled) return
        setResults((prev) => {
          let changed = false
          const nextRows = prev.map((row) => {
            const next = row._id ? freshById.get(row._id) : undefined
            // A rescan still in flight: keep the optimistic INITIAL row so the
            // stale (pre-rescan) verdict doesn't flash back before it resolves.
            if (row._id && baselines.has(row._id)) return row
            if (!next) return row
            if (row.status === next.status && row.scanStatus === next.scanStatus && row.updatedAt === next.updatedAt) {
              return row
            }
            changed = true
            return { ...row, status: next.status, scanStatus: next.scanStatus, updatedAt: next.updatedAt }
          })
          // Return the same reference when nothing moved — avoids needless
          // re-renders and re-running the highlights effect each poll tick.
          return changed ? nextRows : prev
        })
      } catch {
        // Transient poll errors are ignored; the next tick retries.
      }
    }

    const timer = window.setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [pending])

  return {
    documentName: documentMeta?.documentName,
    results,
    loadingHistory,
    historyError,
    reloadHistory,
    refresh,
    pending,
    ensureHighlights,
    rescanItems,
    drawerOpen,
    openDrawer,
    closeDrawer,
    items,
    loadingItems,
    itemsError,
    query,
    setQuery,
    filters,
    setFilters,
    selectedIds,
    toggleSelect,
    reloadItems,
    scanning,
    scanError,
    runScan
  }
}
